import { ClaimLinesAction, ClaimLinesActionTypes } from './claim-lines.actions';
import { ClaimLine } from '@ecosure-suite/shared/util-entities';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { Pagination } from '@ecosure-suite/shared/utils-helpers';

export const CLAIMLINES_FEATURE_KEY = 'clClaimLines';

/**
 * Interface for the 'ClaimLines' data used in
 *  - ClaimLinesState, and
 *  - claimLinesReducer
 *
 *  Note: replace if already defined in another module
 */
export const claimLineEntityAdapter: EntityAdapter<
  ClaimLine
> = createEntityAdapter<ClaimLine>({
  selectId: (claimLine: ClaimLine) => claimLine.id,
  sortComparer: false
});

export interface ClaimLinesState extends EntityState<ClaimLine> {
  selectedIds: number[]; // list of ClaimLines; analogous to a sql normalized table
  selectedByStatusIds: number[];
  selectedByStatusPage: Pagination;
  selectedByAssigneeIds: number[];
  selectedByAssigneePage: Pagination;
  selectedByCurrentUserIds: number[];
  selectedByCurrentUserPage: Pagination;
  selectedId?: number; // which ClaimLines record has been selected
  loaded: boolean; // has the ClaimLines list been loaded
  loading: boolean;
  error?: any; // last none error (if any)
}

export interface ClaimLinesPartialState {
  readonly [CLAIMLINES_FEATURE_KEY]: ClaimLinesState;
}

export const initialState: ClaimLinesState = claimLineEntityAdapter.getInitialState(
  {
    selectedIds: [],
    selectedByStatusIds: [],
    selectedByStatusPage: {
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: 25,
      totalPages: 0
    },
    selectedByAssigneeIds: [],
    selectedByAssigneePage: {
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: 25,
      totalPages: 0
    },
    selectedByCurrentUserIds: [],
    selectedByCurrentUserPage: {
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: 25,
      totalPages: 0
    },
    loaded: false,
    loading: false
  }
);

export function claimLinesReducer(
  state: ClaimLinesState = initialState,
  action: ClaimLinesAction
): ClaimLinesState {
  switch (action.type) {
    case ClaimLinesActionTypes.LOAD_CLAIM_LINE:
    case ClaimLinesActionTypes.CREATE_CLAIM_LINE:
    case ClaimLinesActionTypes.EDIT_CLAIM_LINE:
    case ClaimLinesActionTypes.DELETE_CLAIM_LINE:
    case ClaimLinesActionTypes.SUBMIT_CLAIM_LINE:
    case ClaimLinesActionTypes.LIST_CLAIM_LINE_BY_STATUS:
    case ClaimLinesActionTypes.LIST_CLAIM_LINE_BY_ASSIGNEE:
    case ClaimLinesActionTypes.LIST_CLAIM_LINE_BY_CURRENT_USER:
    case ClaimLinesActionTypes.ASSIGN_CLAIM_LINE:
    case ClaimLinesActionTypes.VERIFY_CLAIM_LINE:
    case ClaimLinesActionTypes.APPROVE_CLAIM_LINE:
    case ClaimLinesActionTypes.RETURN_CLAIM_LINE:
    case ClaimLinesActionTypes.REJECT_CLAIM_LINE:
      return {
        ...state,
        loaded: false,
        loading: true,
        error: undefined
      };

    case ClaimLinesActionTypes.LOAD_CLAIM_LINE_SUCCESS:
      return {
        ...claimLineEntityAdapter.addMany(action.payload, state),
        selectedIds: action.payload.map(claimLine => claimLine.id),
        loaded: true,
        loading: false
      };

    case ClaimLinesActionTypes.LIST_CLAIM_LINE_BY_STATUS_SUCCESS:
      return {
        ...claimLineEntityAdapter.addMany(action.payload.claimLines, state),
        selectedByStatusIds: action.payload.claimLines.map(
          claimLine => claimLine.id
        ),
        selectedByStatusPage: action.payload.page,
        loaded: true,
        loading: false
      };

    case ClaimLinesActionTypes.LIST_CLAIM_LINE_BY_ASSIGNEE_SUCCESS:
      return {
        ...claimLineEntityAdapter.addMany(action.payload.claimLines, state),
        selectedByAssigneeIds: action.payload.claimLines.map(
          claimLine => claimLine.id
        ),
        selectedByAssigneePage: action.payload.page,
        loaded: true,
        loading: false
      };

    case ClaimLinesActionTypes.LIST_CLAIM_LINE_BY_CURRENT_USER_SUCCESS:
      return {
        ...claimLineEntityAdapter.addMany(action.payload.claimLines, state),
        selectedByCurrentUserIds: action.payload.claimLines.map(
          claimLine => claimLine.id
        ),
        selectedByCurrentUserPage: action.payload.page,
        loaded: true,
        loading: false
      };

    case ClaimLinesActionTypes.CREATE_CLAIM_LINE_SUCCESS:
      return {
        ...claimLineEntityAdapter.addOne(action.payload, state),
        loaded: true,
        loading: false
      };

    case ClaimLinesActionTypes.EDIT_CLAIM_LINE_SUCCESS:
    case ClaimLinesActionTypes.SUBMIT_CLAIM_LINE_SUCCESS:
    case ClaimLinesActionTypes.ASSIGN_CLAIM_LINE_SUCCESS:
    case ClaimLinesActionTypes.VERIFY_CLAIM_LINE_SUCCESS:
    case ClaimLinesActionTypes.APPROVE_CLAIM_LINE_SUCCESS:
    case ClaimLinesActionTypes.RETURN_CLAIM_LINE_SUCCESS:
    case ClaimLinesActionTypes.REJECT_CLAIM_LINE_SUCCESS:
      return {
        ...claimLineEntityAdapter.updateOne(
          {
            id: action.payload.id,
            changes: action.payload
          },
          state
        ),
        loaded: true,
        loading: false
      };

    case ClaimLinesActionTypes.DELETE_CLAIM_LINE_SUCCESS:
      return {
        ...claimLineEntityAdapter.removeOne(action.payload.id, state),
        loaded: true,
        loading: false
      };

    case ClaimLinesActionTypes.SELECT_CLAIM_LINE:
      return {
        ...state,
        selectedId: action.payload
      };

    case ClaimLinesActionTypes.LOAD_CLAIM_LINE_FAIL:
    case ClaimLinesActionTypes.CREATE_CLAIM_LINE_FAIL:
    case ClaimLinesActionTypes.EDIT_CLAIM_LINE_FAIL:
    case ClaimLinesActionTypes.DELETE_CLAIM_LINE_FAIL:
    case ClaimLinesActionTypes.SUBMIT_CLAIM_LINE_FAIL:
    case ClaimLinesActionTypes.LIST_CLAIM_LINE_BY_STATUS_FAIL:
    case ClaimLinesActionTypes.LIST_CLAIM_LINE_BY_ASSIGNEE_FAIL:
    case ClaimLinesActionTypes.LIST_CLAIM_LINE_BY_CURRENT_USER_FAIL:
    case ClaimLinesActionTypes.ASSIGN_CLAIM_LINE_FAIL:
    case ClaimLinesActionTypes.VERIFY_CLAIM_LINE_FAIL:
    case ClaimLinesActionTypes.APPROVE_CLAIM_LINE_FAIL:
    case ClaimLinesActionTypes.RETURN_CLAIM_LINE_FAIL:
    case ClaimLinesActionTypes.REJECT_CLAIM_LINE_FAIL:
      return {
        ...state,
        loaded: false,
        loading: false,
        error: action.payload
      };

    default:
      return state;
  }
}
